// Simple in-memory cache for recipes
// Persists during the browser session, clears on refresh

const cache = {
  recipes: new Map(),      // id -> recipe data
  recipesBySlug: new Map(), // "username/slug" -> recipe data
  lists: new Map(),          // listKey -> array of recipes
  profiles: new Map()        // username/userId -> profile data
};

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

const currentViewerKey = () => {
  if (typeof localStorage === 'undefined' || !localStorage.getItem('adminToken')) return null;
  try {
    const id = JSON.parse(localStorage.getItem('user') || '{}').id;
    const token = localStorage.getItem('adminToken');
    return id && token ? `${id}:${token}` : null;
  } catch {
    return null;
  }
};

const readableRecipe = (cached) => {
  if (!cached || Date.now() - cached.timestamp >= CACHE_DURATION) return null;
  // Public content is safe across login/logout. Private content is only
  // reusable by the same authenticated account that populated the cache.
  if (cached.data?.is_public === false) {
    const viewerKey = currentViewerKey();
    if (!viewerKey || cached.viewerKey !== viewerKey) return null;
  }
  return cached.data;
};

const recipeEntry = (recipe, timestamp) => ({
  data: recipe,
  timestamp,
  viewerKey: recipe?.is_public === false ? currentViewerKey() : null,
});

export const recipeCache = {
  // Get recipe by ID
  getById(id) {
    const cached = cache.recipes.get(id);
    return readableRecipe(cached);
  },

  // Get recipe by username/slug
  getBySlug(username, slug) {
    const key = `${username}/${slug}`;
    const cached = cache.recipesBySlug.get(key);
    return readableRecipe(cached);
  },

  // Get recipe list by key (e.g., "user:123" or "all")
  getList(listKey) {
    const cached = cache.lists.get(listKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  },

  // Get profile by username or userId
  getProfile(key) {
    const cached = cache.profiles.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  },

  // Store recipe (stores by both ID and slug if available)
  set(recipe, username = null) {
    const timestamp = Date.now();
    
    if (recipe.id) {
      cache.recipes.set(recipe.id, recipeEntry(recipe, timestamp));
    }
    
    if (username && recipe.slug) {
      const key = `${username}/${recipe.slug}`;
      cache.recipesBySlug.set(key, recipeEntry(recipe, timestamp));
    }
  },

  // Store a list of recipes. When the owner's username is known, also key
  // each recipe by username/slug so pretty-URL pages render instantly from
  // cache (no skeleton flash) when a card is clicked.
  setList(listKey, recipes, username = null) {
    const timestamp = Date.now();
    cache.lists.set(listKey, { data: recipes, timestamp });
    
    // Also cache individual recipes from the list
    recipes.forEach(recipe => {
      if (recipe.id) {
        cache.recipes.set(recipe.id, recipeEntry(recipe, timestamp));
      }
      if (username && recipe.slug) {
        cache.recipesBySlug.set(`${username}/${recipe.slug}`, recipeEntry(recipe, timestamp));
      }
    });
  },

  // Store a profile
  setProfile(key, profile) {
    const timestamp = Date.now();
    cache.profiles.set(key, { data: profile, timestamp });
    // Also cache by id and username if available
    if (profile.id) {
      cache.profiles.set(profile.id, { data: profile, timestamp });
    }
    if (profile.username) {
      cache.profiles.set(profile.username, { data: profile, timestamp });
    }
  },

  // Invalidate a specific recipe
  invalidate(id) {
    cache.recipes.delete(id);
    // Also clear from slug cache if we find it
    for (const [key, value] of cache.recipesBySlug.entries()) {
      if (value.data.id === id) {
        cache.recipesBySlug.delete(key);
        break;
      }
    }
    // Clear all lists since they may contain the invalidated recipe
    cache.lists.clear();
  },

  invalidateSlug(username, slug) {
    cache.recipesBySlug.delete(`${username}/${slug}`);
  },

  // Invalidate all lists (call after creating/deleting recipes)
  invalidateLists() {
    cache.lists.clear();
  },

  // Clear all cache
  clear() {
    cache.recipes.clear();
    cache.recipesBySlug.clear();
    cache.lists.clear();
    cache.profiles.clear();
  }
};

export default recipeCache;
